import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calculator, Zap, Wind, Settings, Plug } from "lucide-react";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Home Page Component
const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_c17e2164-ab3d-484d-95a2-57c72f37949b/artifacts/psc51iwj_logo%20basa.png" 
            alt="BASA Energie" 
            className="mx-auto mb-6 h-24 object-contain"
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Calculateur de Devis Énergétiques
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Obtenez rapidement une estimation pour vos projets énergétiques
          </p>
        </div>

        {/* Three Category Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Solar Panels */}
          <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-white border-2 border-transparent hover:border-blue-200">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Panneaux Photovoltaïques
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Calculez le prix de votre installation solaire basée sur votre consommation annuelle
            </p>
            <Link to="/solar">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg">
                Calculer mon devis
              </Button>
            </Link>
          </Card>

          {/* Air Conditioning */}
          <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-white border-2 border-transparent hover:border-green-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Wind className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Climatisation
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Découvrez nos prix pour les climatiseurs LG de 2.5kW à 6kW
            </p>
            <Link to="/aircon">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg">
                Voir les prix
              </Button>
            </Link>
          </Card>

          {/* Charging Stations */}
          <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-white border-2 border-transparent hover:border-blue-200">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Plug className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Bornes de Recharge
            </h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Consultez nos différentes bornes de recharge ainsi que les accessoires.
            </p>
            <Link to="/charging">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg">
                Explorer les options
              </Button>
            </Link>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center mt-16 text-gray-600">
          <p className="mb-2">Une question ? Besoin d'aide ?</p>
          <a 
            href="https://www.basaenergie.be/contact" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            Contactez-nous pour un rendez-vous
          </a>
        </div>
      </div>
    </div>
  );
};

// Solar Calculator Component
const SolarCalculator = () => {
  const [consumption, setConsumption] = useState("");
  const [selectedConfig, setSelectedConfig] = useState("");
  const [configurations, setConfigurations] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const response = await axios.get(`${API}/solar/configurations`);
        setConfigurations(response.data);
        if (response.data.length > 0) {
          setSelectedConfig(response.data[0].id);
        }
      } catch (error) {
        toast.error("Erreur lors du chargement des configurations");
        console.error(error);
      }
    };

    fetchConfigurations();
  }, []);

  const calculateSolar = async () => {
    if (!consumption || parseFloat(consumption) <= 0) {
      toast.error("Veuillez entrer une consommation valide");
      return;
    }

    if (!selectedConfig) {
      toast.error("Veuillez sélectionner un type d'installation");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/solar/calculate/${selectedConfig}`, {
        annual_consumption_kwh: parseFloat(consumption)
      });
      setResult(response.data);
      toast.success("Calcul effectué avec succès !");
    } catch (error) {
      toast.error("Erreur lors du calcul");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c17e2164-ab3d-484d-95a2-57c72f37949b/artifacts/psc51iwj_logo%20basa.png" 
                alt="BASA Energie" 
                className="h-16 object-contain"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Calculateur Panneaux Photovoltaïques
            </h1>
          </div>

          {/* Calculator Card */}
          <Card className="p-8 mb-6">
            <div className="space-y-6">
              <div>
                <Label htmlFor="configSelect" className="text-lg font-semibold">
                  Type d'installation
                </Label>
                <select
                  id="configSelect"
                  value={selectedConfig}
                  onChange={(e) => setSelectedConfig(e.target.value)}
                  className="w-full mt-2 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {configurations.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="consumption" className="text-lg font-semibold">
                  Votre consommation annuelle (kWh)
                </Label>
                <Input
                  id="consumption"
                  type="number"
                  placeholder="Ex: 4000"
                  value={consumption}
                  onChange={(e) => setConsumption(e.target.value)}
                  className="mt-2 text-lg"
                />
              </div>

              <Button 
                onClick={calculateSolar}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
              >
                {loading ? "Calcul en cours..." : "Calculer mon devis"}
              </Button>
            </div>
          </Card>

          {/* Results */}
          {result && (
            <Card className="p-8 bg-white border-2 border-blue-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Votre Devis</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-lg">
                  <span>Consommation annuelle:</span>
                  <span className="font-semibold">{result.annual_consumption_kwh} kWh</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Puissance recommandée:</span>
                  <span className="font-semibold">{result.kwc_needed} kWc</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-2xl font-bold text-blue-600">
                    <span>Prix estimé:</span>
                    <span>{result.total_price}€</span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Disclaimer */}
          <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-gray-700 text-center">
              <strong>Important :</strong> Cette offre est soumise à validation après inspection du bâtiment et confirmation de la faisabilité du projet.<br/>
              <strong>Nos Marques Premium :</strong> Solaredge, SMA, Fronius.
            </p>
            <div className="text-center mt-4">
              <a 
                href="https://www.basaenergie.be/contact" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-green-600 hover:bg-green-700">
                  Demander un rendez-vous
                </Button>
              </a>
            </div>
          </div>

          <div className="text-center mt-6">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Air Conditioning Component
const AirConditioning = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API}/aircon/models`);
        setModels(response.data.models);
      } catch (error) {
        toast.error("Erreur lors du chargement des modèles");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c17e2164-ab3d-484d-95a2-57c72f37949b/artifacts/psc51iwj_logo%20basa.png" 
                alt="BASA Energie" 
                className="h-16 object-contain"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Climatisation LG
            </h1>
          </div>

          {/* Models Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {models.map((model, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <Wind className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {model.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{model.power}</p>
                  <div className="text-3xl font-bold text-green-600">
                    {model.price}€
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <p className="text-gray-700 text-center mb-4">
              <strong>Important :</strong> Cette offre est soumise à validation après inspection du bâtiment et confirmation de la faisabilité du projet.<br/>
              Les tarifs indiqués comprennent la fourniture et la pose de 8 mètres de tuyauterie frigorifique, ainsi que le montage et la mise en service de l'installation.
            </p>
            <div className="text-center">
              <a 
                href="https://www.basaenergie.be/contact" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-green-600 hover:bg-green-700">
                  Demander un rendez-vous
                </Button>
              </a>
            </div>
          </div>

          <div className="text-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Charging Stations Component
const ChargingStations = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API}/charging/models`);
        setModels(response.data.models);
      } catch (error) {
        toast.error("Erreur lors du chargement des modèles");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c17e2164-ab3d-484d-95a2-57c72f37949b/artifacts/psc51iwj_logo%20basa.png" 
                alt="BASA Energie" 
                className="h-16 object-contain"
              />
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Bornes de Recharge
            </h1>
          </div>

          {/* Models Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {models.map((model, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="text-center">
                  <Plug className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    {model.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{model.power}</p>
                  <div className="text-2xl font-bold text-blue-600">
                    {model.price}€
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <p className="text-gray-700 text-center mb-4">
              <strong>Important :</strong> Cette offre reste conditionnée à l'inspection du bâtiment et à la validation de la faisabilité du projet.<br/>
              Les tarifs s'appliquent pour une distance de câblage inférieure ou égale à 6 mètres (câble d'alimentation de Borne).<br/>
              Le prix comprend également le montage complet et la mise en service de l'installation.
            </p>
            <div className="text-center">
              <a 
                href="https://www.basaenergie.be/contact" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-green-600 hover:bg-green-700">
                  Demander un rendez-vous
                </Button>
              </a>
            </div>
          </div>

          <div className="text-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = () => {
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("solar_premium_no_battery");

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/admin/secret-pricing-management/all-pricing`);
        const pricingData = {};
        response.data.forEach(item => {
          pricingData[item.category] = item.config_data;
        });
        setPricing(pricingData);
      } catch (error) {
        toast.error("Erreur lors du chargement des prix");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  const updatePricing = async (category, configData) => {
    try {
      await axios.put(`${API}/admin/secret-pricing-management/update`, {
        category,
        config_data: configData
      });
      toast.success("Prix mis à jour avec succès !");
      setPricing(prev => ({ ...prev, [category]: configData }));
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
      console.error(error);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center mb-8">
              <Settings className="w-8 h-8 text-gray-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-800">
                Administration - Gestion des Prix
              </h1>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap space-x-2 mb-8 border-b">
              <button
                className={`pb-2 px-3 text-sm ${activeTab === "solar_premium_no_battery" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("solar_premium_no_battery")}
              >
                Premium sans batterie
              </button>
              <button
                className={`pb-2 px-3 text-sm ${activeTab === "solar_premium_with_battery" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("solar_premium_with_battery")}
              >
                Premium avec batterie
              </button>
              <button
                className={`pb-2 px-3 text-sm ${activeTab === "solar_fox_no_battery" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("solar_fox_no_battery")}
              >
                FOX ESS sans batterie (Nouveau produit)
              </button>
              <button
                className={`pb-2 px-3 text-sm ${activeTab === "solar_fox_with_battery" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("solar_fox_with_battery")}
              >
                FOX ESS avec batterie (Nouveau produit)
              </button>
              <button
                className={`pb-2 px-4 ${activeTab === "air_conditioning" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("air_conditioning")}
              >
                Climatisation
              </button>
              <button
                className={`pb-2 px-4 ${activeTab === "charging_stations" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600"}`}
                onClick={() => setActiveTab("charging_stations")}
              >
                Bornes de Recharge
              </button>
            </div>

            {/* Solar Panel Configurations */}
            {(activeTab === "solar_premium_no_battery" || activeTab === "solar_premium_with_battery" || 
              activeTab === "solar_fox_no_battery" || activeTab === "solar_fox_with_battery") && 
              pricing[activeTab] && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold">Configuration {pricing[activeTab].name || activeTab}</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Prix par kWc (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={pricing[activeTab].price_per_kwc || 0}
                      onChange={(e) => {
                        const newConfig = { ...pricing[activeTab], price_per_kwc: parseFloat(e.target.value) };
                        updatePricing(activeTab, newConfig);
                      }}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Pourcentage supplémentaire (%)</Label>
                    <Input
                      type="number"
                      value={pricing[activeTab].overhead_percentage || 0}
                      onChange={(e) => {
                        const newConfig = { ...pricing[activeTab], overhead_percentage: parseFloat(e.target.value) };
                        updatePricing(activeTab, newConfig);
                      }}
                      className="mt-2"
                    />
                  </div>
                  {(activeTab === "solar_premium_with_battery" || activeTab === "solar_fox_with_battery") && (
                    <div>
                      <Label>Prix batterie (€)</Label>
                      <Input
                        type="number"
                        value={pricing[activeTab].battery_price || 0}
                        onChange={(e) => {
                          const newConfig = { ...pricing[activeTab], battery_price: parseFloat(e.target.value) };
                          updatePricing(activeTab, newConfig);
                        }}
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Air Conditioning Config */}
            {activeTab === "air_conditioning" && pricing.air_conditioning && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Configuration Climatisation</h3>
                  <Button 
                    onClick={() => {
                      const newModel = { name: "Nouveau modèle", power: "0kW", price: 0 };
                      const newModels = [...pricing.air_conditioning.models, newModel];
                      const newConfig = { ...pricing.air_conditioning, models: newModels };
                      updatePricing("air_conditioning", newConfig);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    + Ajouter un modèle
                  </Button>
                </div>
                <div className="space-y-4">
                  {pricing.air_conditioning.models.map((model, index) => (
                    <div key={index} className="grid md:grid-cols-4 gap-4 p-4 border rounded">
                      <div>
                        <Label>Nom du modèle</Label>
                        <Input
                          value={model.name}
                          onChange={(e) => {
                            const newModels = [...pricing.air_conditioning.models];
                            newModels[index].name = e.target.value;
                            const newConfig = { ...pricing.air_conditioning, models: newModels };
                            updatePricing("air_conditioning", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Puissance</Label>
                        <Input
                          value={model.power}
                          onChange={(e) => {
                            const newModels = [...pricing.air_conditioning.models];
                            newModels[index].power = e.target.value;
                            const newConfig = { ...pricing.air_conditioning, models: newModels };
                            updatePricing("air_conditioning", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          value={model.price}
                          onChange={(e) => {
                            const newModels = [...pricing.air_conditioning.models];
                            newModels[index].price = parseFloat(e.target.value);
                            const newConfig = { ...pricing.air_conditioning, models: newModels };
                            updatePricing("air_conditioning", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={() => {
                            if (pricing.air_conditioning.models.length > 1) {
                              const newModels = pricing.air_conditioning.models.filter((_, i) => i !== index);
                              const newConfig = { ...pricing.air_conditioning, models: newModels };
                              updatePricing("air_conditioning", newConfig);
                            } else {
                              toast.error("Il doit y avoir au moins un modèle");
                            }
                          }}
                          variant="destructive"
                          className="w-full"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Charging Stations Config */}
            {activeTab === "charging_stations" && pricing.charging_stations && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold">Configuration Bornes de Recharge</h3>
                  <Button 
                    onClick={() => {
                      const newModel = { name: "Nouveau modèle", power: "0kW", price: 0 };
                      const newModels = [...pricing.charging_stations.models, newModel];
                      const newConfig = { ...pricing.charging_stations, models: newModels };
                      updatePricing("charging_stations", newConfig);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    + Ajouter un modèle
                  </Button>
                </div>
                <div className="space-y-4">
                  {pricing.charging_stations.models.map((model, index) => (
                    <div key={index} className="grid md:grid-cols-4 gap-4 p-4 border rounded">
                      <div>
                        <Label>Nom du modèle</Label>
                        <Input
                          value={model.name}
                          onChange={(e) => {
                            const newModels = [...pricing.charging_stations.models];
                            newModels[index].name = e.target.value;
                            const newConfig = { ...pricing.charging_stations, models: newModels };
                            updatePricing("charging_stations", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Puissance</Label>
                        <Input
                          value={model.power}
                          onChange={(e) => {
                            const newModels = [...pricing.charging_stations.models];
                            newModels[index].power = e.target.value;
                            const newConfig = { ...pricing.charging_stations, models: newModels };
                            updatePricing("charging_stations", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>Prix (€)</Label>
                        <Input
                          type="number"
                          value={model.price}
                          onChange={(e) => {
                            const newModels = [...pricing.charging_stations.models];
                            newModels[index].price = parseFloat(e.target.value);
                            const newConfig = { ...pricing.charging_stations, models: newModels };
                            updatePricing("charging_stations", newConfig);
                          }}
                          className="mt-2"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          onClick={() => {
                            if (pricing.charging_stations.models.length > 1) {
                              const newModels = pricing.charging_stations.models.filter((_, i) => i !== index);
                              const newConfig = { ...pricing.charging_stations, models: newModels };
                              updatePricing("charging_stations", newConfig);
                            } else {
                              toast.error("Il doit y avoir au moins un modèle");
                            }
                          }}
                          variant="destructive"
                          className="w-full"
                        >
                          Supprimer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 text-center">
              <Link to="/" className="text-blue-600 hover:text-blue-800">
                ← Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/solar" element={<SolarCalculator />} />
          <Route path="/aircon" element={<AirConditioning />} />
          <Route path="/charging" element={<ChargingStations />} />
          <Route path="/admin/secret-pricing-management" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;